import { ref } from "vue";
import KeenSlider, { KeenSliderInstance } from "keen-slider";
import type { DsButtonRefType } from "@allwyn/design-system/src/button/ds-button-reference";
import useCustomNavigationPlugin from "@allwyn/utils/keen-slider/plugins/keen-custom-navigation-plugin";
import { whenDomReady } from "@sazka/utils/document-ready";

await whenDomReady();

let keenSlider: KeenSliderInstance = null;
const useDataSelectorBase = (name: string) => `[data-ds-slider="${name}"]`;

/**
 * Updates the visibility of slide stepper elements based on the current slide index.
 * Hides previous and next slide steppers, shows only the current one.
 */
const updateSlideStepperVisibility = (currentIndex: number): void => {
    // Hide all stepper elements
    document.querySelectorAll("[data-slide-stepper-index]").forEach((stepper) => {
        stepper.classList.add("d-none");
    });

    const currentStepper = document.querySelector(`[data-slide-stepper-index="${currentIndex}"]`);
    currentStepper?.classList.remove("d-none");
};

function initSlider() {
    const root = document.querySelector(useDataSelectorBase("root"));
    if (!root) return;

    const carousel = root.querySelector<HTMLElement>(useDataSelectorBase("carousel"));
    if (!carousel) return;

    const btnPrev = ref<DsButtonRefType>(root.querySelector(useDataSelectorBase("slider-prev")));
    const dots = ref<HTMLElement>(root.querySelector(useDataSelectorBase("slider-dots")));
    const btnNext = ref<DsButtonRefType>(root.querySelector(useDataSelectorBase("slider-next")));

    keenSlider = new KeenSlider(
        carousel,
        {
            selector: ".ds-rich-text-slider__track-item",
            loop: false,
            mode: "snap",
            slides: {
                perView: 1,
                spacing: 0,
            },
            renderMode: "precision",
            created: () => {
                carousel.classList.add("ds-rich-text-slider--initialized");
            },
            slideChanged: (slider) => {
                if (!slider.track?.details) return;

                const currentSlide = slider.slides[slider.track.details.rel];
                if (currentSlide) {
                    const slideHeight = currentSlide.offsetHeight;
                    carousel.style.height = `${slideHeight}px`;
                }
            },
        },
        [useCustomNavigationPlugin(btnPrev, btnNext, dots)]
    );

    keenSlider.on("slideChanged", (slider) => {
        if (!slider.track?.details) return;

        const currentIndex = slider.track.details.rel;
        updateSlideStepperVisibility(currentIndex);
    });

    keenSlider.on("created", () => {
        updateSlideStepperVisibility(0);
    });

    // Reposition slider on window load to ensure proper sizing
    window.addEventListener("load", () => {
        keenSlider?.update();
    });
}

initSlider();
